<template>
  <OppleModal 
    class="sun-dialog" 
    :visible="innerVisible" 
    :is-show-warn="false" 
    :is-show-btn="true" 
    @onClose="handleCancel"
    @onOk="handleConfirm"
  >
    <div slot="title">
      <OppleTitle>
        {{ dialogTitle }}
      </OppleTitle>
    </div>
    <div class="sun-dialog__body">
      <!-- 类型选择 -->
      <div class="sun-dialog__section">
        <el-radio-group v-model="timeType" class="type-radio-group">
          <!-- 时间（只有定时序列自动化才显示） -->
          <el-radio v-if="showTypeSelect" label="time">
            {{ $t('ifttt.timing') }}
          </el-radio>
          <el-radio label="sunrise">
            {{ $t('iftttset.sunrise') }}
          </el-radio>
          <el-radio label="sunset">
            {{ $t('iftttset.sunset') }}
          </el-radio>
        </el-radio-group>
      </div>

      <!-- 定时设置 -->
      <div v-if="timeType === 'time'" class="sun-dialog__section">
        <div class="sun-dialog__label">
          {{ $t('iftttset.timeSetting') }}
        </div>
        <el-time-picker
          v-model="timeValue"
          :editable="true"
          class="sun-dialog__time-picker"
          format="HH:mm"
          value-format="HH:mm"
          :placeholder="$t('iftttset.SelecTime')"
          popper-class="sun-dialog-time-popper"
        />
      </div>

      <!-- 日出日落设置 -->
      <template v-if="timeType === 'sunrise' || timeType === 'sunset'">
        <!-- 时间设置 -->
        <div class="sun-dialog__section">
          <div class="sun-dialog__label">
            {{ $t('iftttset.timeSetting') }}
          </div>

          <el-select 
            v-model="selectedOption" 
            size="small"
            class="sun-dialog__select" 
            :popper-append-to-body="false" 
            popper-class="sun-dialog-popper" 
            @change="handleOptionChange"
          >
            <el-option
              v-for="option in currentTimeOptions"
              :key="option.value"
              :label="option.label"
              :value="option.value"
            />
          </el-select>

          <div v-if="showInput" class="sun-dialog__time">
            <el-input-number 
              v-model="offsetMinute" 
              :min="0" 
              :max="240" 
              size="small" 
              class="sun-dialog__input"
            />

            <span class="sun-dialog__unit"> {{ $t('tianmu.minutes') }} </span>
          </div>

          <div class="sun-dialog__tip">
            <i class="el-icon-warning" />
            <span>{{ $t('ifttt.supportSunriseSunsetOffset') }}</span>
          </div>
        </div>
      </template>
    </div>

    <span slot="footer" class="sun-dialog__footer">
      <el-button type="primary" @click="handleConfirm">{{ $t('iftttset.sure') }}</el-button>

      <el-button @click="handleCancel">{{ $t('iftttset.cancle') }}</el-button>
    </span>
  </OppleModal>
</template>

<script>
import OppleModal from '@/components/OppleModal/index.vue'
import OppleTitle from '@/components/OppleTitle/index.vue'
export default {
  name: 'IfftttSunDialog',
  components: {OppleModal, OppleTitle },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    initData: {
      type: Object,
      default: null,
    },
    showTypeSelect: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      innerVisible: false,

      // 类型：time=时间，sunrise=日出，sunset=日落
      timeType: 'sunrise',

      // 定时时间
      timeValue: '',

      // 选中的时间选项：1=前，2=时，3=后
      selectedOption: 1,

      // 是否显示输入框（日出时、日落时不显示）
      showInput: true,

      // 偏移分钟
      offsetMinute: 25,

      // 经纬度
      longitude: null,
      latitude: null,

      // 时间选项
      timeOptions: {
        sunrise: [
          { label: `${this.$t('iftttset.sunrise')}${this.$t('base.before')}`, value: 1 },
          { label: `${this.$t('iftttset.sunrise')}${this.$t('iftttset.hour')}`, value: 2 },
          { label: `${this.$t('iftttset.sunrise')}${this.$t('base.after')}`, value: 3 }
        ],
        sunset: [
          { label: `${this.$t('iftttset.sunset')}${this.$t('base.before')}`, value: 1 },
          { label: `${this.$t('iftttset.sunset')}${this.$t('iftttset.hour')}`, value: 2 },
          { label: `${this.$t('iftttset.sunset')}${this.$t('base.after')}`, value: 3 }
        ]
      },
    }
  },

  computed: {
    currentTimeOptions() {
      return this.timeType === 'sunrise' ? this.timeOptions.sunrise : this.timeOptions.sunset
    },
    dialogTitle() {
      if (this.showTypeSelect) {
        return this.$t('iftttset.timeSetting')
      }
      return this.$t('ifttt.sunriseSunset')
    }
  },

  watch: {
    visible: {
      immediate: true,
      handler(val) {
        
        this.innerVisible = val
        if (val) {
          if (this.initData) {
            this.initFormData()
          } else {
            this.resetForm()
            if ((this.timeType === 'sunrise' || this.timeType === 'sunset') && this.longitude === null) {
              this.getLocation()
            }
          }
        }
      },
    },
    timeType() {
      // 切换类型时，重置选中值为第一个选项
      if (this.timeType === 'sunrise' || this.timeType === 'sunset') {
        this.selectedOption = 2
        this.showInput = false
      }
    }
  },

  methods: {
    initFormData() {
      if (this.initData) {
        // 判断是定时还是日出日落
        if (this.initData.TimeMode === 0 && this.initData.StartHour !== undefined) {
          // 定时
          this.timeType = 'time'
          const hour = String(this.initData.StartHour).padStart(2, '0')
          const minute = String(this.initData.StartMinute || 0).padStart(2, '0')
          this.timeValue = `${hour}:${minute}`
        } else if (this.initData.TimeMode !== undefined || this.initData.Mode !== undefined) {
          // 日出日落
          const timeMode = this.initData.TimeMode != null ? this.initData.TimeMode : this.initData.Mode
          this.timeType = timeMode === 1 ? 'sunrise' : 'sunset'
          this.longitude = this.initData.Longitude
          this.latitude = this.initData.Latitude
          
          const offset = this.initData.Offset || 0
          if (offset === 0) {
            // 时
            this.selectedOption = 2
            this.showInput = false
            this.offsetMinute = 0
          } else if (offset > 0) {
            // 后
            this.selectedOption = 3
            this.showInput = true
            this.offsetMinute = offset
          } else {
            // 前
            this.selectedOption = 1
            this.showInput = true
            this.offsetMinute = Math.abs(offset)
          }
        }
      }
    },

    resetForm() {
      // 如果showTypeSelect为true，默认选中时间；否则默认选中日出
      this.timeType = this.showTypeSelect ? 'time' : 'sunrise'
      this.timeValue = ''
      this.selectedOption = 2
      this.showInput = false
      this.offsetMinute = 25
    },

    getLocation() {
      
      const projectInfo = this.$store?.state?.projectinfor || {}
      const projectPos = (projectInfo.Pos && projectInfo.Pos[0]) || {}
      const projectLongitude = projectPos.Longitude 
      const projectLatitude = projectPos.Latitude
      const hasProjectLocation = this.isValidProjectLocation(projectLongitude, projectLatitude)

      if (!hasProjectLocation) {
          this.$message.warning(this.$t('projectmsg.pleaseSetLatitudeLongitude'))
          return
      }

      if (hasProjectLocation) {
        this.longitude = Number(projectLongitude)
        this.latitude = Number(projectLatitude)
        return
      }

      // if ('geolocation' in navigator) {
      //   navigator.geolocation.getCurrentPosition(
      //     (position) => {
      //       this.longitude = position.coords.longitude
      //       this.latitude = position.coords.latitude
      //     },
      //     (error) => {
      //       console.warn('获取位置失败:', error.message)
      //     },
      //     {
      //       enableHighAccuracy: true,
      //       timeout: 10000,
      //       maximumAge: 0
      //     }
      //   )
      // } else {
      //   console.warn('浏览器不支持地理位置功能')
      // }
    },

    isValidProjectLocation(longitude, latitude) {
      const lon = Number(longitude)
      const lat = Number(latitude)
      return Number.isFinite(lon) && Number.isFinite(lat) && !(lon === 0 && lat === 0)
    },

    handleOptionChange(value) {
      // 2=时，不显示输入框
      this.showInput = value !== 2
    },

    handleConfirm() {
      
      let result = {}
      
      if (this.timeType === 'time') {
        // 定时
        if (!this.timeValue) {
          this.$message.warning(this.$t('iftttset.SelecTime'))
          return
        }
        const [hour, minute] = this.timeValue.split(':')
        result = {
          TimeMode: 0,
          StartHour: parseInt(hour),
          StartMinute: parseInt(minute),
          TimeLabel: this.timeValue
        }
      } else {
        // 日出日落
        let offset = 0
        let label = ''
        const projectInfo = this.$store?.state?.projectinfor || {}
        const projectPos = (projectInfo.Pos && projectInfo.Pos[0]) || {}
        const projectLongitude = projectPos.Longitude
        const projectLatitude = projectPos.Latitude
        const hasProjectLocation = this.isValidProjectLocation(projectLongitude, projectLatitude)

        if (!hasProjectLocation) {
          this.$message.warning(this.$t('projectmsg.pleaseSetLatitudeLongitude'))
          return
        }

        const currentOptions = this.currentTimeOptions
        const selectedOpt = currentOptions.find(opt => opt.value === this.selectedOption)
        if (this.selectedOption === 1) {
          // 前
          offset = -this.offsetMinute
          label = `${selectedOpt.label} ${this.offsetMinute} ${this.$t('tianmu.minutes')}`
        } else if (this.selectedOption === 3) {
          // 后
          offset = this.offsetMinute
          label = `${selectedOpt.label} ${this.offsetMinute} ${this.$t('tianmu.minutes')}`
        } else {
          // 时
          label = selectedOpt.label
        }
        
        result = {
          TimeMode: this.timeType === 'sunrise' ? 1 : 2,
          Offset: offset,
          Longitude: hasProjectLocation ? Number(projectLongitude) : this.longitude,
          Latitude: hasProjectLocation ? Number(projectLatitude) : this.latitude,
          TimeLabel: label
        }
      }

      this.$emit('confirm', result)
      this.$emit('close')
    },

    handleCancel() {
      this.$emit('close')
    },
  },
}
</script>
<style lang="scss" scoped>
.sun-dialog {
  ::v-deep .opModal_btns{
    width: 70%;
    justify-content: space-between;
    margin: 0 auto;
  }
  ::v-deep .opModal{
    width: 500px;
    .el-icon-warning {
      margin-right: 5px;
      font-size: 16px;
      color: #BEC4D3;
    }
  }
  ::v-deep .opModal_body {
    justify-content: center;
  }

  &__body {
    width: 280px;
    min-height: 200px;
    overflow: hidden; // BFC
  }

  &__section:first-child {
    width: 80%;
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
    ::v-deep .el-radio-group {
      width: 100%;
      display: flex;
      justify-content: space-between;
    }
  }

  &__section:not(:first-child) {
    margin-bottom: 20px;
  }

  &__label {
    font-size: 14px;
    margin-bottom: 10px;
  }

  &__time {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }

  &__select {
    width: 80%;
  }

  &__time-picker {
    width: 80%;
  }

  &__input {
    width: 80%;
    margin: 10px 0px;
  }

  &__unit {
    margin-left: 10px;
    font-size: 14px;
  }

  &__tip {
    display: flex;
    align-items: center;
    margin-top: 6px;
    font-size: 12px;
    color: #999;
    span{
      color: #BEC4D3;
    }
  }

  &__footer {
    text-align: center;
  }
}

.type-radio-group {
  width: 80%;
  display: flex;
  justify-content: space-between;
}
</style>

<style lang="scss">
/* 时间选择器弹出层样式 - 需要全局样式 */
.sun-dialog-time-popper {
  z-index: 9999 !important;
}
</style>
